import React, { useState } from "react";
import { submitVoiceInquiry } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Send, Shield, Info, Check } from "lucide-react";

/**
 * Qualifikations-Formular für alle 6 SAT Stimmen™ Produkte.
 * Ein Modal-Formular oder inline verwendbar. Aktiviert Kinderschutz-Block
 * nur, wenn `voice.is_minor_gated === true`.
 */
const VoiceInquiryForm = ({ voice, onSuccess, embedded = false }) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", country: "Schweiz",
    motivation: "",
    format_preference: "",
    availability: "",
    additional_info: "",
    is_minor: false,
    minor_age: "",
    guardian_name: "",
    guardian_email: "",
    guardian_consent: false,
    consent_gdpr: false,
    referral_source: "",
    language: "de",
  });

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const validateStep = () => {
    if (!form.first_name.trim()) return "Bitte gib deinen Vornamen an.";
    if (!form.email.trim()) return "E-Mail wird benötigt.";
    if (form.motivation.trim().length < 10) return "Bitte teile uns 1–2 Sätze zu deiner Motivation mit.";
    if (!form.consent_gdpr) return "Bitte bestätige die Datenschutz-Einwilligung.";
    if (voice.is_minor_gated && form.is_minor) {
      if (!form.guardian_name.trim()) return "Bitte gib den Namen des Erziehungsberechtigten an.";
      if (!form.guardian_email.trim()) return "Bitte gib die E-Mail des Erziehungsberechtigten an.";
      if (!form.guardian_consent) return "Bitte bestätige die Zustimmung des Erziehungsberechtigten.";
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const payload = { ...form, product_slug: voice.slug };
      if (!voice.is_minor_gated) {
        // Kinderschutz-Felder für andere Produkte weglassen
        delete payload.is_minor;
        delete payload.minor_age;
        delete payload.guardian_name;
        delete payload.guardian_email;
        delete payload.guardian_consent;
      } else if (!form.is_minor) {
        // Wenn Kinderstimme aber nicht für einen Minderjährigen
        payload.guardian_name = "";
        payload.guardian_email = null;
        payload.guardian_consent = false;
      }
      if (form.minor_age === "" || form.minor_age === null) payload.minor_age = null;
      if (!payload.guardian_email) payload.guardian_email = null;
      await submitVoiceInquiry(payload);
      setDone(true);
      toast.success("Anfrage erhalten · wir melden uns innerhalb von 48 Stunden.");
      onSuccess?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Anfrage konnte nicht gesendet werden.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className={`${embedded ? "" : "max-w-2xl mx-auto"} rounded-2xl border border-white/10 bg-[#0f1a35]/60 backdrop-blur p-8 text-center`} data-testid="voice-inquiry-success">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-5" style={{ background: `${voice.accent}22`, color: voice.accent }}>
          <Check size={26} />
        </div>
        <h3 className="serif text-2xl font-light mb-3 text-white">Deine Anfrage ist bei uns.</h3>
        <p className="text-white/70 leading-relaxed">
          Wir sichten jede Anfrage persönlich. Innerhalb von 48&nbsp;Stunden meldet sich das Team mit einem Vorschlag für ein Klärungsgespräch.
        </p>
      </div>
    );
  }

  const inputCls = "w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 px-4 py-3 text-sm placeholder-white/30 text-white";

  return (
    <form onSubmit={submit} className={`${embedded ? "" : "max-w-2xl mx-auto"} rounded-2xl border border-white/10 bg-[#0f1a35]/60 backdrop-blur p-6 sm:p-8 space-y-5`} data-testid="voice-inquiry-form">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase" style={{ color: voice.accent }}>
        <Send size={12} /> Anfrage · {voice.label}
      </div>
      <h3 className="serif text-2xl font-light text-white">Sag uns kurz, wer du bist.</h3>
      <p className="text-sm text-white/60 -mt-3">
        Wir sichten jede Anfrage persönlich — deine Angaben helfen uns, das richtige Format vorzuschlagen.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input required data-testid="voice-input-first-name" className={inputCls} placeholder="Vorname *" value={form.first_name} onChange={e => update("first_name", e.target.value)} />
        <input data-testid="voice-input-last-name" className={inputCls} placeholder="Nachname" value={form.last_name} onChange={e => update("last_name", e.target.value)} />
        <input required type="email" data-testid="voice-input-email" className={inputCls} placeholder="E-Mail *" value={form.email} onChange={e => update("email", e.target.value)} />
        <input data-testid="voice-input-phone" className={inputCls} placeholder="Telefon" value={form.phone} onChange={e => update("phone", e.target.value)} />
      </div>

      <textarea
        required
        data-testid="voice-input-motivation"
        className={`${inputCls} min-h-[110px]`}
        placeholder="Was zieht dich zu diesem Format? Was möchtest du klären? *"
        value={form.motivation}
        onChange={e => update("motivation", e.target.value)}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <select data-testid="voice-input-format" className={inputCls} value={form.format_preference} onChange={e => update("format_preference", e.target.value)}>
          <option value="">Format-Wunsch (optional)</option>
          <option value="seminar">Seminar / Gruppen-Format</option>
          <option value="retreat">Retreat</option>
          <option value="1-1">1:1 Intensiv</option>
          <option value="inhouse">Inhouse / Firma</option>
          <option value="flexible">Ich bin offen</option>
        </select>
        <input data-testid="voice-input-availability" className={inputCls} placeholder="Verfügbarkeit (z. B. Q2 2026)" value={form.availability} onChange={e => update("availability", e.target.value)} />
      </div>

      <input data-testid="voice-input-referral" className={inputCls} placeholder="Wie hast du uns gefunden?" value={form.referral_source} onChange={e => update("referral_source", e.target.value)} />

      <textarea
        data-testid="voice-input-additional"
        className={`${inputCls} min-h-[80px]`}
        placeholder="Weitere Infos (optional)"
        value={form.additional_info}
        onChange={e => update("additional_info", e.target.value)}
      />

      {/* Kinderschutz-Block */}
      {voice.is_minor_gated && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 space-y-4" data-testid="voice-minor-block">
          <div className="flex items-start gap-3">
            <Shield size={18} className="text-amber-400 mt-0.5 shrink-0" />
            <div className="text-sm text-white/80 leading-relaxed">
              <b className="text-amber-300">Kinderschutz.</b> Die SAT&nbsp;Kinderstimme™ ist ein Bildungsformat für Minderjährige.
              Die Buchung ist nur mit ausdrücklicher Einwilligung eines Erziehungsberechtigten möglich.
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" data-testid="voice-input-is-minor" checked={form.is_minor} onChange={e => update("is_minor", e.target.checked)} className="mt-1 accent-amber-400" />
            <span className="text-sm text-white/80">Die Anfrage betrifft ein Kind bzw. eine*n Jugendliche*n unter 18.</span>
          </label>

          {form.is_minor && (
            <div className="space-y-3 pl-6">
              <input type="number" min="4" max="17" data-testid="voice-input-minor-age" className={inputCls} placeholder="Alter des Kindes *" value={form.minor_age} onChange={e => update("minor_age", e.target.value)} />
              <input data-testid="voice-input-guardian-name" className={inputCls} placeholder="Name Erziehungsberechtigte*r *" value={form.guardian_name} onChange={e => update("guardian_name", e.target.value)} />
              <input type="email" data-testid="voice-input-guardian-email" className={inputCls} placeholder="E-Mail Erziehungsberechtigte*r *" value={form.guardian_email} onChange={e => update("guardian_email", e.target.value)} />
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" data-testid="voice-input-guardian-consent" checked={form.guardian_consent} onChange={e => update("guardian_consent", e.target.checked)} className="mt-1 accent-amber-400" />
                <span className="text-xs text-white/70 leading-relaxed">Als Erziehungsberechtigte*r stimme ich der Anfrage und Kontaktaufnahme zu und bestätige, dass ich das Kind in diesem Prozess begleite.</span>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3">
        <Info size={16} className="text-white/50 mt-0.5 shrink-0" />
        <p className="text-xs text-white/60 leading-relaxed">{voice.disclaimer}</p>
      </div>

      {/* DSGVO */}
      <label className="flex items-start gap-3 cursor-pointer">
        <input required type="checkbox" data-testid="voice-input-gdpr" checked={form.consent_gdpr} onChange={e => update("consent_gdpr", e.target.checked)} className="mt-1" style={{ accentColor: voice.accent }} />
        <span className="text-xs text-white/70 leading-relaxed">
          Ich habe die <a href="/datenschutz" className="underline hover:text-white">Datenschutzerklärung</a> gelesen und willige der Verarbeitung meiner Angaben zur Anfrage-Bearbeitung ein.
        </span>
      </label>

      <button
        type="submit"
        disabled={busy}
        data-testid="voice-inquiry-submit"
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-4 font-medium tracking-wide transition disabled:opacity-60"
        style={{ background: voice.accent, color: "#081328" }}
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {busy ? "Sende Anfrage …" : "Anfrage absenden"}
      </button>
    </form>
  );
};

export default VoiceInquiryForm;
